import { Menu, MenuItem } from "@mui/material";
import { MuiBox, MuiTypography, SubmitLoaderButton } from "components/atoms";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  openAdminModal,
  closeAdminModal,
} from "store/slices/common/adminModal.slice";
import { Button } from "components/ui";
import { EllipsisVertical, Pencil, Trash } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DeleteRoleAction } from "store/middlewares/membersAndRoles/roles";
import checkAccess from "utils/checkAccess";

/* ---------- delete confirm (same file) ---------- */
const DeleteRoleConfirm = ({ row }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = () => {
    setIsSubmitting(true);
    dispatch(
      DeleteRoleAction({
        id: row.id,
        onSuccess: () => {
          dispatch(closeAdminModal());
          setIsSubmitting(false);
        },
        onError: () => {
          dispatch(closeAdminModal());
          setIsSubmitting(false);
        },
      }),
    );
  };

  return (
    <div className="grid gap-5 p-1">
      <div className="flex items-center gap-3.5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
          <Trash className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          Delete this role?
        </h2>
      </div>

      <ul className="grid gap-2">
        <li className="flex gap-2.5 text-sm leading-6 text-secondary-foreground">
          <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
          <strong>{row.name}</strong> will be permanently removed from the
          system.
        </li>
        <li className="flex gap-2.5 text-sm leading-6 text-secondary-foreground">
          <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
          This action cannot be undone.
        </li>
      </ul>

      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          size="lg"
          disabled={isSubmitting}
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <SubmitLoaderButton
          type="button"
          variant="destructive"
          size="lg"
          btnText="Delete role"
          isDisable={isSubmitting}
          onClick={handleDelete}
        />
      </div>
    </div>
  );
};
/* ----------------------------------------------- */

const Roles = ({ row }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const ActionMenuArray = [
    checkAccess("update_roles") && {
      label: "Edit",
      icon: <Pencil size={16} color="#475569" />,
      onClick: () => navigate(`edit/${row.id}`),
    },
    checkAccess("delete_roles") &&
      row.assignCount === 0 && {
        label: "Delete",
        icon: <Trash size={16} color="#475569" />,
        onClick: () => {
          dispatch(
            openAdminModal({
              title: "",
              content: <DeleteRoleConfirm row={row} />,
            }),
          );
        },
      },
  ].filter(Boolean);

  const [actionMenu, setActionMenu] = useState(null);
  const handleActionMenuOpen = (event) => {
    setActionMenu(event.currentTarget);
  };
  const handleActionMenuClose = () => {
    setActionMenu(null);
  };

  return (
    <MuiBox className="flex h-full items-center justify-center gap-3">
      <Button
        className="size-7 bg-[#f8f8fa]"
        mode="icon"
        variant="ghost"
        onClick={handleActionMenuOpen}
      >
        <EllipsisVertical />
      </Button>
      <Menu
        anchorEl={actionMenu}
        open={Boolean(actionMenu)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          " & .MuiMenu-paper": {
            mt: "10px",
            background: "#fff",
            boxShadow:
              "0 4px 6px -1px #e2e8f0, 0 2px 4px -2px #e2e8f0 !important",
          },
        }}
      >
        {ActionMenuArray.map((option) => (
          <MenuItem
            key={option.label}
            onClick={() => {
              handleActionMenuClose();
              option.onClick();
            }}
          >
            <MuiBox sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
              <MuiBox sx={{ width: "fit-content" }}>{option.icon}</MuiBox>
              <MuiTypography
                sx={{ fontWeight: "500", fontSize: "13px", color: "#475569" }}
              >
                {option.label}
              </MuiTypography>
            </MuiBox>
          </MenuItem>
        ))}
      </Menu>
    </MuiBox>
  );
};

export default Roles;